import React from 'react'
import styled from '@emotion/styled'
import {
  useFloating,
  autoUpdate,
  offset,
  type Placement,
  shift,
  autoPlacement,
} from '@floating-ui/react-dom'
import { useRenderTimeoutControl } from '@mobi/utils/hooks/useRenderTimeoutControl'
import {
  colors,
  font,
  layering,
  radius,
  shadow,
  spacing,
} from '@mobi/component-library/Foundations'

const enum LocalConstants {
  AnimationTimingMs = 125,
}

type Props = {
  isOpen: boolean
  /** Popover will be placed relative to reference element */
  referenceElRef: React.RefObject<HTMLElement>
  /** Relative to reference, where should popover be placed */
  placement?: Placement
  /** Offset in pixels from reference on main axis */
  offsetYPx?: number
  /** Offset in pixels from reference on cross axis */
  offsetXPx?: number
  /** Allow auto placement with most space available */
  isAutoPlacement?: boolean
  children?: React.ReactNode
  /** Callback to dismiss menu on scroll */
  onDismiss?: () => void
  /** Set to true to make the context menu full width */
  fullWidth?: boolean
}

export const ContextMenu: React.FC<Props> = ({
  isOpen,
  referenceElRef,
  placement = 'bottom',
  offsetYPx = 0,
  offsetXPx = 0,
  isAutoPlacement = false,
  children,
  onDismiss,
  fullWidth = false,
}) => {
  const shouldRenderContextMenu = useRenderTimeoutControl({
    shouldRender: isOpen,
    timeoutMs: LocalConstants.AnimationTimingMs as number,
  })

  React.useEffect(() => {
    if (!isOpen || !onDismiss) return
    const handleDismissOnScroll = () => onDismiss()
    window.addEventListener('scroll', handleDismissOnScroll)
    return () => window.removeEventListener('scroll', handleDismissOnScroll)
  }, [isOpen, onDismiss])

  if (!shouldRenderContextMenu) return null

  return (
    <ContextMenuComponent
      isOpen={isOpen}
      referenceElRef={referenceElRef}
      placement={placement}
      offsetYPx={offsetYPx}
      offsetXPx={offsetXPx}
      isAutoPlacement={isAutoPlacement}
      fullWidth={fullWidth}
    >
      {children}
    </ContextMenuComponent>
  )
}

// ===============
// Local Component
// ===============

const ContextMenuComponent: React.FC<
  Omit<React.ComponentProps<typeof ContextMenu>, 'onDismiss'>
> = ({
  isOpen,
  referenceElRef,
  placement,
  offsetYPx,
  offsetXPx,
  isAutoPlacement,
  children,
  fullWidth,
}) => {
  const middleware = React.useMemo(() => {
    const extras = [
      offset({
        mainAxis: offsetYPx ?? 0,
        crossAxis: offsetXPx ?? 0,
      }),
      shift(),
    ]
    if (isAutoPlacement) extras.push(autoPlacement())
    return extras
  }, [isAutoPlacement, offsetYPx, offsetXPx])

  const { floatingStyles, refs } = useFloating({
    placement,
    middleware,
    whileElementsMounted: autoUpdate,
    elements: { reference: referenceElRef.current },
  })

  return (
    <ContextMenuStyled
      ref={refs.setFloating}
      data-testid='Overlay.ContextMenu'
      style={{ ...floatingStyles, width: fullWidth ? '100%' : undefined }}
    >
      <div className={isOpen ? 'context-menu--open' : 'context-menu--closed'}>{children}</div>
    </ContextMenuStyled>
  )
}

// ======
// Styles
// ======

const ContextMenuStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  zIndex: layering.pagePopoverElements,

  '> div': {
    display: 'flex',
    flexDirection: 'column',
    padding: spacing.smx1,
    background: colors.white,
    alignItems: 'center',
    gap: spacing.smx1,
    border: `0.1rem solid ${colors.neutral[200]}`,
    borderRadius: radius.lgx2,
    boxShadow: shadow.lg,
    boxSizing: 'border-box',
    transformOrigin: 'top right',
    opacity: 0,
    scale: 0.93,
    transition: `opacity ${LocalConstants.AnimationTimingMs}ms cubic-bezier(0.34, 1.56, 0.64, 1), scale ${LocalConstants.AnimationTimingMs}ms cubic-bezier(0.34, 1.56, 0.64, 1)`,

    '> h4': {
      padding: `0 ${spacing.smx2}`,
      margin: 0,
      marginBottom: spacing.smx2,
      color: colors.black,
      fontFamily: font.family.primary,
      fontSize: font.size.md.fontSize,
      letterSpacing: font.size.md.letterSpacing,
      lineHeight: font.size.md.lineHeight,
      fontWeight: font.weight.semibold,
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },

    '> button': {
      display: 'flex',
      alignSelf: 'stretch',
      justifyContent: 'center',

      '> div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'stretch',
        width: '100%',
        gap: spacing.smx1,
      },
    },

    '&.context-menu--open': {
      opacity: 1,
      scale: 1,
    },
  },
})
